import Seo from '@comp/seo'
import Link from 'next/link'
import { motion } from 'framer-motion'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'

const EASE = [0.22, 1, 0.36, 1] as const

// Single source of truth for the registration link. Always points to the
// Monohub partner-application form, never to a local form.
const APPLY_URL = 'https://hub.monofor.com/become-partner'

export default function BecomePartnerPage() {
  return (
    <Layout>
      <Seo
        title="Become a Partner | Monofor"
        description="Resell, implement, integrate, or refer. Monofor's partner program rewards every kind of identity expert with margins, enablement, and a product engineered to win."
        path="/become-partner"
      />
      <main className="bg-surface text-ink-primary">
        <HeroSection />
        <ValueProps />
        <TrustBand />
        <PartnerTracks />
        <Benefits />
        <IdealProfile />
        <HowItWorks />
        <FinalCallToAction />
      </main>
    </Layout>
  )
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-surface pt-24 pb-20 lg:pt-32 lg:pb-24">
      <BackgroundField />
      <Container size="xl" className="relative">
        <div className="grid items-center gap-16 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
          <div>
            <motion.div
              suppressHydrationWarning
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="brand">Partner Program</Eyebrow>
            </motion.div>

            <motion.div
              suppressHydrationWarning
              className="mt-6 max-w-3xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-2xl">
                Build identity.
                <br />
                Win together.
              </Heading>
            </motion.div>

            <motion.div
              suppressHydrationWarning
              className="mt-8 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.1 }}
            >
              <Text size="xl" tone="secondary">
                Resell, implement, integrate, or refer. Monofor&rsquo;s partner
                program rewards every kind of identity expert with margins,
                enablement, and a product engineered to win, across IAM, PAM,
                and IGA.
              </Text>
            </motion.div>

            <motion.div
              suppressHydrationWarning
              className="mt-10 flex flex-wrap items-center gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.18 }}
            >
              <Button
                href={APPLY_URL}
                target="_blank"
                variant="primary"
                size="lg"
              >
                Apply to become a partner →
              </Button>
              <Button href="/contact" variant="outline" size="lg">
                Talk to partnerships
              </Button>
            </motion.div>
          </div>

          {/* Right-rail at-a-glance card */}
          <motion.div
            suppressHydrationWarning
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE, delay: 0.15 }}
            className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
          >
            <Eyebrow>At a glance</Eyebrow>
            <ul className="mt-5 space-y-4">
              {AT_A_GLANCE.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <CheckGlyph />
                  </span>
                  <div>
                    <div className="text-body-md font-medium text-ink-primary">
                      {item.label}
                    </div>
                    <Text size="sm" tone="tertiary" className="mt-0.5">
                      {item.description}
                    </Text>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Container>
    </section>
  )
}

const AT_A_GLANCE = [
  {
    label: 'Four ways to partner',
    description: 'Reseller, Solution, Technology, or Referral: pick the model that fits your business.',
  },
  {
    label: 'Margins that scale',
    description: 'Tiered discounts that grow with your pipeline. No deal-registration roulette.',
  },
  {
    label: 'Enablement included',
    description: 'Free training and certification for your sales, pre-sales, and delivery teams.',
  },
  {
    label: 'A product that closes',
    description: 'Self-hosted or cloud, one platform for IAM + PAM + IGA, deployable in minutes.',
  },
]

// ---------------------------------------------------------------------------
// Why Monofor: three value pillars
// ---------------------------------------------------------------------------

function ValueProps() {
  return (
    <Section tone="subtle" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>Why partner with Monofor</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            A short list of vendors
            <br />
            you can actually differentiate on.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            Identity is a crowded market. Monofor gives your team a story that
            stands out in the room, and a product that holds up after the demo.
          </Text>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {VALUE_PILLARS.map((p, i) => (
            <ValueCard key={p.title} pillar={p} index={i} />
          ))}
        </div>
      </Container>
    </Section>
  )
}

interface Pillar {
  title: string
  body: string
  stat?: { value: string; label: string }
}

const VALUE_PILLARS: Pillar[] = [
  {
    title: 'One platform, three products',
    body: 'Monosign (IAM), Monopam (PAM), and Monosync (IGA) ship as one suite. Customers consolidate three vendors into one and you sell a bigger, stickier deal.',
    stat: { value: '3-in-1', label: 'IAM + PAM + IGA' },
  },
  {
    title: 'Self-hosted or cloud',
    body: 'Win deals in regulated industries (finance, public sector, healthcare, telco) where data residency, sovereignty, or air-gap deployment are non-negotiable.',
    stat: { value: '100%', label: 'self-host capable' },
  },
  {
    title: 'Built since 2010, proven at scale',
    body: 'Not a startup, not a hyperscaler. A partner-friendly specialist with 15+ enterprise customers and millions of identities already in production.',
    stat: { value: '50M+', label: 'identities secured' },
  },
]

function ValueCard({ pillar, index }: { pillar: Pillar; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, ease: EASE, delay: index * 0.06 }}
      className="flex h-full flex-col rounded-3xl border border-line-subtle bg-surface p-8 shadow-card transition hover:shadow-card-hover"
    >
      <div className="text-display-md font-medium text-brand-900 tabular-nums">
        {pillar.stat?.value}
      </div>
      <div className="mt-1 text-caption uppercase tracking-[0.14em] text-ink-tertiary">
        {pillar.stat?.label}
      </div>
      <div className="mt-8 text-body-xl font-medium text-ink-primary">
        {pillar.title}
      </div>
      <Text size="md" tone="secondary" className="mt-3">
        {pillar.body}
      </Text>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Partner tracks
// ---------------------------------------------------------------------------

function PartnerTracks() {
  return (
    <Section tone="default" density="spacious">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>Choose your track</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Four ways to partner.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            Whether you sell, deliver, build, or refer, there&rsquo;s a track
            tuned to your business. Pick one or combine several over time.
          </Text>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:gap-8">
          {TRACKS.map((t, i) => (
            <TrackCard key={t.title} track={t} index={i} />
          ))}
        </div>
      </Container>
    </Section>
  )
}

interface Track {
  title: string
  tagline: string
  description: string
  fitFor: string[]
}

const TRACKS: Track[] = [
  {
    title: 'Reseller',
    tagline: 'Sell Monofor under your own contract.',
    description:
      'Buy licenses at tiered partner discount and resell to your customers. Dedicated channel manager, deal protection, and renewals support.',
    fitFor: ['Value-added resellers', 'Distributors', 'MSSPs'],
  },
  {
    title: 'Solution Partner',
    tagline: 'Implement and integrate Monofor as part of your services.',
    description:
      'Add Monofor to your delivery catalog. Certified architects bill engagement hours; we provide pre-sales engineering and architecture review.',
    fitFor: ['Systems integrators', 'Identity consultancies', 'Boutique advisories'],
  },
  {
    title: 'Technology Partner',
    tagline: 'Build an integration with our APIs.',
    description:
      'Connect your product to Monofor&rsquo;s SCIM, REST, and SAML/OIDC endpoints. Listed in the Monofor integrations directory; joint go-to-market on qualifying use cases.',
    fitFor: ['SaaS vendors', 'Security tooling', 'HRIS / ITSM platforms'],
  },
  {
    title: 'Referral',
    tagline: 'Refer a deal, earn a fee.',
    description:
      'No commitment. Hand off qualified opportunities through the partner portal and earn a referral fee when they convert.',
    fitFor: ['Consultants', 'Influencers', 'Existing customers'],
  },
]

function TrackCard({ track, index }: { track: Track; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, ease: EASE, delay: index * 0.06 }}
      className="relative overflow-hidden rounded-3xl border border-line bg-surface p-8 shadow-card transition hover:border-brand-300 hover:shadow-card-hover lg:p-10"
    >
      <div className="flex items-baseline justify-between gap-4">
        <Heading as="h3" size="display-sm">
          {track.title}
        </Heading>
        <span className="rounded-pill bg-brand-50 px-3 py-1 text-caption font-semibold uppercase tracking-[0.12em] text-brand-800">
          Track
        </span>
      </div>
      <Text size="lg" className="mt-3 text-ink-primary font-medium">
        {track.tagline}
      </Text>
      <Text size="md" tone="secondary" className="mt-4">
        {track.description}
      </Text>
      <div className="mt-6 flex flex-wrap gap-2">
        {track.fitFor.map((f) => (
          <span
            key={f}
            className="inline-flex items-center rounded-pill bg-surface-muted px-3 py-1 text-caption text-ink-secondary"
          >
            {f}
          </span>
        ))}
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Benefits grid
// ---------------------------------------------------------------------------

function Benefits() {
  return (
    <Section tone="muted" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>What you get</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Every track. Every benefit.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            We don&rsquo;t gatekeep training, leads, or co-marketing behind
            arbitrary tiers. Sign the agreement and the full toolkit is yours.
          </Text>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {BENEFITS.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl bg-surface p-6 shadow-card transition hover:shadow-card-hover"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <b.icon />
                </span>
                <div className="text-body-md font-medium text-ink-primary">
                  {b.title}
                </div>
              </div>
              <Text size="sm" tone="secondary" className="mt-4">
                {b.description}
              </Text>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

const BENEFITS = [
  {
    title: 'Tiered margins',
    description:
      'Discounts grow with your pipeline. No surprise re-pricing at renewal; terms locked into the partner agreement.',
    icon: PercentIcon,
  },
  {
    title: 'Free training & certification',
    description:
      'Sales, pre-sales, and implementation tracks. On-demand video plus quarterly live workshops with our engineers.',
    icon: GradCapIcon,
  },
  {
    title: 'Co-marketing & lead gen',
    description:
      'Joint webinars, case studies, and curated MQLs from our marketing funnel. Funded campaigns for premier partners.',
    icon: MegaphoneIcon,
  },
  {
    title: 'Pre-sales engineering',
    description:
      'Loop in our solutions architects for demos, PoCs, and architecture reviews. Free for all active partners.',
    icon: WrenchIcon,
  },
  {
    title: 'NFR licenses',
    description:
      'Production-grade Monosign + Monopam + Monosync for your internal use. Use it, demo from it, learn it.',
    icon: KeyIcon,
  },
  {
    title: 'Partner portal',
    description:
      'Register deals, track pipeline, download marketing assets, and submit support tickets, all in one console.',
    icon: WindowIcon,
  },
  {
    title: 'Dedicated partner manager',
    description:
      'A human, not a queue. Active resellers and solution partners get a named contact with quarterly business reviews.',
    icon: UserIcon,
  },
  {
    title: 'Roadmap briefings',
    description:
      'Quarterly invitation-only sessions where our product team shares what&rsquo;s shipping next, before it&rsquo;s public.',
    icon: CompassIcon,
  },
  {
    title: 'Implementation playbooks',
    description:
      'Battle-tested architectures for SSO consolidation, PAM rollout, and joiner-mover-leaver, built from real deployments.',
    icon: BookIcon,
  },
]

// ---------------------------------------------------------------------------
// Ideal profile
// ---------------------------------------------------------------------------

function IdealProfile() {
  return (
    <Section tone="default" density="default">
      <Container size="xl">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <div>
            <Eyebrow>Who&rsquo;s a good fit</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              Built for partners
              <br />
              who sell on outcomes.
            </Heading>
            <Text size="xl" tone="secondary" className="mt-6">
              Our best partners share three things in common: they understand
              identity is strategic, they sell to enterprise IT and security
              leaders, and they win on delivery, not list price.
            </Text>
            <Text size="lg" tone="secondary" className="mt-4">
              If your customers ask &ldquo;can it run in our datacenter?&rdquo;
              or &ldquo;does it cover PAM <em>and</em> IGA?&rdquo;, we&rsquo;re
              probably a good fit.
            </Text>
          </div>

          <ul className="space-y-4">
            {PROFILES.map((p, i) => (
              <motion.li
                key={p.title}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                className="flex items-start gap-4 rounded-2xl border border-line-subtle bg-surface-subtle px-6 py-5"
              >
                <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-900 text-white text-caption font-semibold">
                  {i + 1}
                </span>
                <div>
                  <div className="text-body-lg font-medium text-ink-primary">
                    {p.title}
                  </div>
                  <Text size="md" tone="secondary" className="mt-1.5">
                    {p.description}
                  </Text>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </Container>
    </Section>
  )
}

const PROFILES = [
  {
    title: 'Managed security service providers',
    description:
      'You run identity for your customers as a service. Monofor lets you offer IAM, PAM, and IGA without stitching three contracts.',
  },
  {
    title: 'Systems integrators with an identity practice',
    description:
      'You bill delivery hours and need a vendor that scales with you, not one that races you to the customer.',
  },
  {
    title: 'IAM and security consultancies',
    description:
      'You advise on identity strategy and want a flexible product to recommend across cloud and on-prem clients.',
  },
  {
    title: 'Cloud and infrastructure resellers',
    description:
      'You move infrastructure and want to attach a high-margin security layer without re-tooling your sales motion.',
  },
  {
    title: 'Industry specialists',
    description:
      'You sell into finance, healthcare, public sector, or telco, markets where self-hosting and audit trails are deal-breakers.',
  },
]

// ---------------------------------------------------------------------------
// How it works (timeline)
// ---------------------------------------------------------------------------

function HowItWorks() {
  return (
    <Section tone="subtle" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>How it works</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            From application to first deal
            <br />
            in weeks, not quarters.
          </Heading>
        </div>

        <ol className="mt-14 grid gap-px overflow-hidden rounded-3xl bg-line-subtle md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <li key={step.title} className="relative bg-surface p-8 lg:p-10">
              <div className="text-caption uppercase tracking-[0.18em] text-brand-700">
                Step {i + 1}
              </div>
              <div className="mt-3 text-display-sm font-medium text-ink-primary">
                {step.title}
              </div>
              <Text size="md" tone="secondary" className="mt-3">
                {step.description}
              </Text>
            </li>
          ))}
        </ol>
      </Container>
    </Section>
  )
}

const STEPS = [
  {
    title: 'Apply',
    description:
      'Submit the partner application on Monohub. Tell us your model, your market, and how you sell today.',
  },
  {
    title: 'Onboard',
    description:
      'Sign the partner agreement and complete certification. 2-3 weeks for sales + technical training.',
  },
  {
    title: 'Sell',
    description:
      'Get co-branded materials, NFR licenses, and access to our pre-sales bench. Register your first deal in the portal.',
  },
  {
    title: 'Grow',
    description:
      'Quarterly business reviews, expansion playbooks, and tiered margin upgrades as your pipeline grows.',
  },
]

// ---------------------------------------------------------------------------
// Closing CTA
// ---------------------------------------------------------------------------

function FinalCallToAction() {
  return (
    <Section tone="inverse" density="default">
      <Container size="xl">
        <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
          <div>
            <Eyebrow tone="inverse">Ready to grow with Monofor?</Eyebrow>
            <Heading
              as="h2"
              size="display-xl"
              tone="inverse"
              className="mt-5"
            >
              Apply now.
              <br />
              Get a partner manager this week.
            </Heading>
            <Text size="xl" className="mt-6 max-w-xl !text-brand-100">
              The application takes about 5 minutes. We&rsquo;ll come back to
              you within two business days with a partner manager and the next
              steps tailored to your track.
            </Text>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-pill bg-white px-7 py-3.5 text-body-md font-medium text-brand-900 transition hover:bg-brand-50"
              >
                Apply on Monohub →
              </a>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-pill border border-white/40 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-white/10"
              >
                Talk to partnerships first
              </Link>
            </div>
            <Text size="sm" className="mt-6 !text-brand-200">
              The registration form lives on{' '}
              <span className="text-white">hub.monofor.com</span>, our
              partner portal. You&rsquo;ll be redirected there to complete your
              application.
            </Text>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, ease: EASE }}
            className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm lg:p-10"
          >
            <div className="text-caption uppercase tracking-[0.18em] text-brand-200">
              Quick FAQ
            </div>
            <dl className="mt-6 space-y-6">
              {FAQ.map((q) => (
                <div key={q.q}>
                  <dt className="text-body-md font-semibold text-white">
                    {q.q}
                  </dt>
                  <dd className="mt-2 text-body-sm text-brand-100">
                    {q.a}
                  </dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </div>
      </Container>
    </Section>
  )
}

const FAQ = [
  {
    q: 'Is there a fee to join?',
    a: 'No. Application and onboarding are free across all four tracks.',
  },
  {
    q: 'Do I need existing identity experience?',
    a: 'For Reseller and Referral, no; we&rsquo;ll train your sales team. Solution and Technology partners benefit from in-house IAM or security expertise.',
  },
  {
    q: 'How long until I can sell?',
    a: 'Most resellers are fully ramped within 2-3 weeks. Solution partners take longer because of architect certification.',
  },
  {
    q: 'Can I combine tracks?',
    a: 'Yes. Many of our partners resell and implement, or build a Technology integration alongside a referral motion.',
  },
]

// ---------------------------------------------------------------------------
// Decorative background field (matches /company)
// ---------------------------------------------------------------------------

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/60 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-partner"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-partner)" />
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline icons (currentColor, 18×18 unless overridden)
// ---------------------------------------------------------------------------

function CheckGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M2.5 6.4 5 8.9 9.5 3.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PercentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="m5 19 14-14M8 7.5A1.5 1.5 0 1 1 5 7.5a1.5 1.5 0 0 1 3 0Zm11 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function GradCapIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 10 12 5l9 5-9 5-9-5Zm4 3v4c2 1.3 4 2 5 2s3-.7 5-2v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MegaphoneIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6l-5 4H4a1 1 0 0 0-1 1Zm12-5 5 6-5 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function WrenchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-2.5 2.5-2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 9a4 4 0 1 1-3.5 5.9L4 21l1.5 1.5-.5-3 3-.5L9 17l1-1 1-1a4 4 0 0 1 3-6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="16" cy="8" r="1" fill="currentColor" />
    </svg>
  )
}

function WindowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6" cy="6.5" r="0.6" fill="currentColor" />
      <circle cx="8.5" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 20c1.2-3.4 4-5 7-5s5.8 1.6 7 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CompassIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m15.5 8.5-5 1.5-1.5 5 5-1.5 1.5-5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 5a2 2 0 0 1 2-2h13v17H6a2 2 0 0 0-2 2V5Zm2 14h13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}
